<template>
  <div class="login-page">
    <div class="login-container">
      <div class="login-card">
        <!-- Tenant Logo -->
        <div class="login-logo">
          <img
            v-if="tenantLogo"
            :src="tenantLogo"
            :alt="tenantName ?? ''"
            class="logo-image"
          />
          <div v-else class="logo-placeholder">
            {{ tenantName || 'Franchise Factory' }}
          </div>
        </div>

        <div class="login-header">
          <h1>{{ t('Login') }}</h1>
          <p class="subtitle">{{ t('Sign in to your account') }}</p>
        </div>

        <form @submit.prevent="handleLogin" class="login-form">
          <!-- Error Message -->
          <div v-if="errorMessage" class="alert alert-error">
            {{ errorMessage }}
          </div>

          <!-- Username Field -->
          <div class="form-group">
            <label for="username">{{ t('Username') }}</label>
            <input
              id="username"
              v-model="username"
              type="text"
              :placeholder="t('Enter your username')"
              required
              autocomplete="username"
              :disabled="isLoading"
              data-testid="auth-login-username"
            />
          </div>

          <!-- Password Field -->
          <div class="form-group">
            <label for="password">{{ t('Password') }}</label>
            <input
              id="password"
              v-model="password"
              type="password"
              :placeholder="t('Enter your password')"
              required
              autocomplete="current-password"
              :disabled="isLoading"
              data-testid="auth-login-password"
            />
          </div>

          <!-- Forgot Password Link -->
          <div class="form-actions">
            <router-link to="/forgot-password" class="forgot-password-link">
              {{ t('Forgot password?') }}
            </router-link>
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            class="btn btn-primary btn-block"
            :disabled="isLoading"
            data-testid="auth-login-submit"
          >
            <span v-if="!isLoading">{{ t('Sign In') }}</span>
            <span v-else>{{ t('Signing in...') }}</span>
          </button>
        </form>

        <!-- Register Link -->
        <div class="login-footer">
          <p>
            {{ t("Don't have an account?") }}
            <router-link to="/register" class="register-link">
              {{ t('Register') }}
            </router-link>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@/composables/useAuth'
import { useFiltro } from '@/composables/useFiltro'
import { useUserLocale } from '@/composables/useUserLocale'
import { permissionsService } from '@/services/permissionsService'
import { tenantConfig } from '@/services/tenantConfigService'
import { log } from '@/utils/DebugLogger'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const { login } = useAuth()
const filtro = useFiltro()
const { applyUserLocale } = useUserLocale()

// Tenant branding
const tenantLogo = computed<string | null>(() => tenantConfig.get<string>('logo_url'))
const tenantName = computed<string | null>(() => tenantConfig.get<string>('tenant_name'))
const primaryColor = computed<string>(() => tenantConfig.get<string>('primary_color') || '#667eea')
const secondaryColor = computed<string>(() => tenantConfig.get<string>('secondary_color') || '#764ba2')

// Form state
const username = ref<string>('')
const password = ref<string>('')
const isLoading = ref<boolean>(false)
const errorMessage = ref<string>('')

onMounted(() => {
  log('LoginPage', 'onMounted', 'Login page loaded', {
    redirectTo: route.query.redirectTo
  })
})

/**
 * Handle login form submission
 */
async function handleLogin(): Promise<void> {
  try {
    isLoading.value = true
    errorMessage.value = ''

    log('LoginPage', 'handleLogin', 'Attempting login', { username: username.value })

    const result = await login(username.value, password.value)

    if (result.success) {
      log('LoginPage', 'handleLogin', '✅ Login successful - tokens stored in memory')

      try {
        // Initialize permissions with the JWT tokens now in memory
        log('LoginPage', 'handleLogin', 'Loading permissions...')
        await permissionsService.initialize({
          minutesToRefresh: 10,
          autoRefresh: true
        })

        log('LoginPage', 'handleLogin', '✅ Permissions loaded')

        // O app carregou sem sessão, no idioma padrão; passa para o do usuário.
        await applyUserLocale()

        // Initialize filter data (now that permissions are available)
        try {
          log('LoginPage', 'handleLogin', 'Initializing filter data...')
          await filtro.init()
          log('LoginPage', 'handleLogin', '✅ Filter initialized')
        } catch (filtroError) {
          log('LoginPage', 'handleLogin', '⚠️ Filter initialization failed (non-critical):', filtroError)
          // Continue - filter failure shouldn't block login
        }

        // Get redirectTo from query params or default to home
        const redirectQuery = route.query.redirectTo
        const redirectTo = typeof redirectQuery === 'string' ? redirectQuery : '/'

        log('LoginPage', 'handleLogin', `Navigating to: ${redirectTo}`)

        // Use router.push to navigate without page reload (preserves tokens in memory)
        router.push(redirectTo)
      } catch (permError) {
        log('LoginPage', 'handleLogin', '❌ Failed to load permissions:', permError)
        errorMessage.value = t('Login successful but failed to load user data. Please try again.')
      }
    } else {
      log('LoginPage', 'handleLogin', '❌ Login failed:', result.error)
      errorMessage.value = result.error || t('Invalid username or password')
    }
  } catch (error) {
    log('LoginPage', 'handleLogin', '❌ Login error:', error)
    errorMessage.value = t('An error occurred. Please try again.')
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: v-bind(primaryColor);
  padding: 1rem;
}

.login-container {
  width: 100%;
  max-width: 420px;
}

.login-card {
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
}

.login-logo {
  text-align: center;
  margin-bottom: 1.5rem;
}

.logo-image {
  max-width: 180px;
  max-height: 80px;
  height: auto;
  object-fit: contain;
}

.logo-placeholder {
  font-size: 1.5rem;
  font-weight: 700;
  color: v-bind(primaryColor);
  padding: 1rem;
}

.login-header {
  text-align: center;
  margin-bottom: 2rem;
}

.login-header h1 {
  font-size: 1.875rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 0.5rem;
}

.subtitle {
  color: #718096;
  font-size: 0.875rem;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 500;
  color: #2d3748;
  font-size: 0.875rem;
}

.form-group input {
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.2s;
}

.form-group input:focus {
  outline: none;
  border-color: v-bind(primaryColor);
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-group input:disabled {
  background-color: #f7fafc;
  cursor: not-allowed;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: -0.5rem;
}

.forgot-password-link {
  color: v-bind(primaryColor);
  font-size: 0.875rem;
  text-decoration: none;
  transition: color 0.2s;
}

.forgot-password-link:hover {
  color: v-bind(secondaryColor);
  text-decoration: underline;
}

.btn {
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: v-bind(secondaryColor);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-block {
  width: 100%;
}

.alert {
  padding: 0.75rem;
  border-radius: 6px;
  font-size: 0.875rem;
}

.alert-error {
  background-color: #fed7d7;
  color: #c53030;
  border: 1px solid #fc8181;
}

.login-footer {
  margin-top: 1.5rem;
  text-align: center;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;
}

.login-footer p {
  color: #718096;
  font-size: 0.875rem;
}

.register-link {
  color: v-bind(primaryColor);
  font-weight: 500;
  text-decoration: none;
  transition: color 0.2s;
}

.register-link:hover {
  color: v-bind(secondaryColor);
  text-decoration: underline;
}
</style>
